<script setup>
import { ref, onMounted, reactive, computed, inject } from 'vue'
import { RequestService } from '../shared/services/RequestService'
import BcsDropdown from '../shared/components/BcsDropdown.vue'
import { handleErrors } from '../shared/utils/errorHandler'

const { simpleGet, getArrayBuffer } = RequestService()
const functionsBaseUrl = inject('functionsBaseUrl')

const months = ref('1')
const year = ref(new Date().getFullYear())
const noResults = ref(false)
const showReport1 = ref(false)
const showReport2 = ref(false)
const loading = ref(false)
const responseData = reactive({
    data: [],
    total: 0
})
const results = ref('')
const results2 = ref('')
const selectedMonth = computed(() => {
    const monthObj = monthsOptions.find(m => m.value === months.value)
    return monthObj ? monthObj.text : ''
})

const monthsOptions = [
    { value: '1', text: 'January' },
    { value: '2', text: 'February' },
    { value: '3', text: 'March' },
    { value: '4', text: 'April' },
    { value: '5', text: 'May' },
    { value: '6', text: 'June' },
    { value: '7', text: 'July' },
    { value: '8', text: 'August' },
    { value: '9', text: 'September' },
    { value: '10', text: 'October' },
    { value: '11', text: 'November' },
    { value: '12', text: 'December' }
]

const hasData = computed(() => responseData.data.length > 0)

const groupByAgency = (data) => {
    return data.reduce((agencies, item) => {
        if (!agencies[item.agencyName]) {
            agencies[item.agencyName] = { logs: [], total: 0 }
        }
        agencies[item.agencyName].logs.push(item);
        agencies[item.agencyName].total += 1
        return agencies
    }, {})
}

const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    Object.assign(link, {
        href: url,
        download: filename,
        style: { visibility: 'hidden' }
    })

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const getTimestamp = () => {
    const date = new Date()
    return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, '0')}${date.getDate().toString().padStart(2, '0')}`
}

const generateCSV1 = () => {
    if (!hasData.value) return
    const csvRows = responseData.data
        .map(log => `"${log.agencyName || ''}","${log.employerName || ''}"`)
    const csvContent = ['Agency Name,Employer Name', ...csvRows].join('\n')
    downloadCSV(csvContent, `assessments_completed_${getTimestamp()}.csv`)
};

const generateCSV2 = () => {
    if (!hasData.value) return
    const groupedData = groupByAgency(responseData.data)
    const csvRows = Object.entries(groupedData)
        .map(([agencyName, data]) => `"${agencyName}",${data.total}`)
    const csvContent = ['Agency Name,Total Assessments Completed by BCS', ...csvRows].join('\n')
    downloadCSV(csvContent, `assessments_count_${getTimestamp()}.csv`)
}

const handleSubmit = async (event) => {
    event.preventDefault()
    loading.value = true
    noResults.value = false
    showReport1.value = false
    showReport2.value = false

    try {
        const response = await simpleGet(
            'Reports',
            `${functionsBaseUrl}/utilityGateway/api/v1/reports/assessment-complete?eventId=19&month=${months.value}&year=${year.value}`
        )

        if (response.status === 200 && response.data) {
            Object.assign(responseData, response.data)

            if (responseData.total === 0) {
                noResults.value = true;
                return
            }

            const groupedData = groupByAgency(responseData.data)
            results.value = groupedData
            results2.value = responseData.data
            showReport1.value = true
            showReport2.value = true
        } else {
            noResults.value = true;
            handleErrors('API Error or unexpected response:', response)
        }
    } catch (error) {
        handleErrors('Error in API request:', error)
        noResults.value = true
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    year.value = new Date().getFullYear()
})

const assessmentOpen = ref(false)
const taskListOpen = ref(false)
const taskListLoading = ref(false)
const agenciesLoading = ref(false)
const employersLoading = ref(false)
const agenciesLoaded = ref(false)
const selectedAgencyId = ref('')
const selectedEmployerId = ref('')
const selectedPlanYearId = ref('')
const agencies = ref([])
const reportEmployers = ref([])

const agencyOptions = computed(() =>
    agencies.value.map((agency) => ({
        value: agency.id,
        text: agency.name ? `${agency.name} (${agency.id})` : agency.id
    }))
)
const employerOptions = computed(() =>
    reportEmployers.value.map((employer) => ({ value: employer.id, text: employer.name || employer.id }))
)
const selectedEmployer = computed(() =>
    reportEmployers.value.find((employer) => employer.id === selectedEmployerId.value) || null
)
const planYearOptions = computed(() =>
    (selectedEmployer.value?.planYears || []).map((planYear) => ({
        value: planYear.id,
        text: planYear.planStartDate || planYear.id
    }))
)
const canDownloadTaskList = computed(() =>
    Boolean(selectedEmployerId.value && selectedPlanYearId.value) && !taskListLoading.value
)

const loadAgencies = async () => {
    agenciesLoading.value = true
    try {
        const response = await simpleGet(
            'Reports agencies',
            `${functionsBaseUrl}/agenciesGateway/api/v1/agencies`
        )
        agencies.value = response?.data?.data || []
        agenciesLoaded.value = true
    } catch (error) {
        handleErrors('Error loading agencies:', error)
    } finally {
        agenciesLoading.value = false
    }
}

const onAgencyChange = async (option) => {
    const agencyId = option?.value || selectedAgencyId.value
    selectedEmployerId.value = ''
    selectedPlanYearId.value = ''
    reportEmployers.value = []
    if (!agencyId) return
    employersLoading.value = true
    try {
        const response = await simpleGet(
            'Reports employers',
            `${functionsBaseUrl}/utilityGateway/api/v1/reports/employers?agencyId=${encodeURIComponent(agencyId)}`
        )
        reportEmployers.value = response?.data?.data || []
    } catch (error) {
        handleErrors('Error loading employers:', error)
    } finally {
        employersLoading.value = false
    }
}

const onEmployerChange = (option) => {
    const employerId = option?.value || selectedEmployerId.value
    const employer = reportEmployers.value.find((item) => item.id === employerId)
    selectedPlanYearId.value = employer?.planYears?.[0]?.id || ''
}

const toggleTaskList = () => {
    taskListOpen.value = !taskListOpen.value
    if (taskListOpen.value && !agenciesLoaded.value && !agenciesLoading.value) {
        loadAgencies()
    }
}

const downloadTaskList = async () => {
    if (!canDownloadTaskList.value) return
    taskListLoading.value = true
    try {
        const response = await getArrayBuffer(
            'Master task list',
            `${functionsBaseUrl}/utilityGateway/api/v1/reports/master-task-list?employerId=${encodeURIComponent(selectedEmployerId.value)}&planYearId=${encodeURIComponent(selectedPlanYearId.value)}`
        )
        if (!response?.data) return
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const disposition = response.headers?.['content-disposition'] || ''
        const match = disposition.match(/filename="([^"]+)"/)
        const employerName = selectedEmployer.value?.name || 'Employer'
        const planStart = planYearOptions.value.find((opt) => opt.value === selectedPlanYearId.value)?.text || ''
        const fileName = match?.[1] || `${employerName} BCS Pro Master List of Tasks Plan ${planStart}.xlsx`
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    } catch (error) {
        handleErrors('Error downloading master task list:', error)
    } finally {
        taskListLoading.value = false
    }
}
</script>

<template>
    <div class="reports-page">
        <h1 class="text-center">Reports</h1>

        <div class="card report-card">
            <button
                type="button"
                class="report-card-toggle"
                :aria-expanded="assessmentOpen"
                @click="assessmentOpen = !assessmentOpen">
                <span class="report-card-heading">
                    <span class="report-card-title">Assessments Completed by BCS</span>
                    <span class="report-card-desc">
                        See which employers had an assessment completed by BCS in a given month,
                        and how many completions each CAP agency had.
                    </span>
                </span>
                <i class="bi text-primary" :class="assessmentOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </button>
            <div v-show="assessmentOpen" class="report-card-body">
                <form @submit.prevent="handleSubmit">
                    <div class="form-group">
                        <label class="fw-bold text-gray-700" for="months">Choose a month:</label>
                        <BcsDropdown v-model="months" id="months" :dropdown-options="monthsOptions">
                        </BcsDropdown>
                    </div>
                    <div class="form-group">
                        <label class="fw-bold text-gray-700" for="year">Enter year:</label>
                        <input style="color: var(--bs-gray-600)" class="form-control text-16 form-input" type="number"
                            id="year" v-model="year" min="1900" max="2100" required>
                    </div>
                    <button type="submit" :class="{ 'disabled': loading }" :disabled="loading"
                        class="btn btn-primary btn-action-profile d-flex justify-content-center align-items-center">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        {{ loading ? 'Loading...' : 'Generate Report' }}
                    </button>
                </form>

                <div v-if="noResults" class="no-results text-center fw-bold">
                    <h2>NO RESULTS FOUND</h2>
                </div>

                <div v-if="showReport1" class="report">
                    <div class="report-header">
                        <h4 class="fw-bold text-primary">Assessments Completed by BCS in {{ selectedMonth }} {{ year }}</h4>
                        <button class="btn btn-primary text-nowrap" @click="generateCSV1" :disabled="loading || !hasData">
                            Generate CSV
                        </button>
                    </div>
                    <div class="d-flex justify-content-between mt-3">
                        <table class="table table-hover mb-1 border-0">
                            <thead>
                                <tr class="table-active">
                                    <th scope="col" class="text-16 ps-4">
                                        <strong>Agency Name</strong>
                                    </th>
                                    <th scope="col" class="text-16 ps-4">
                                        <strong>Employer Name</strong>
                                    </th>
                                </tr>
                            </thead>
                            <tbody v-for="log in results2" :key="log.id">
                                <tr>
                                    <td>{{ log.agencyName }}</td>
                                    <td>{{ log.employerName }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-if="showReport2" class="report">
                    <div class="report-header">
                        <h4 class="fw-bold text-primary">Count of Assessments Completed by BCS for each CAP agency in {{ selectedMonth }} {{ year }}</h4>
                        <button class="btn btn-primary text-nowrap" @click="generateCSV2" :disabled="loading || !hasData">
                            Generate CSV
                        </button>
                    </div>
                    <div class="d-flex flex-column mt-3">
                        <table class="table table-hover mb-1 border-0">
                            <thead>
                                <tr class="table-active">
                                    <th scope="col" class="text-16 ps-4">
                                        <strong>Agency Name</strong>
                                    </th>
                                    <th scope="col" class="text-16 ps-4">
                                        <strong>Total Assessments Completed by BCS</strong>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(value, key) in results" :key="key">
                                    <td class="text-primary fw-bold">{{ key }}</td>
                                    <td class="fw-bold">{{ value.total }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <div class="card report-card">
            <button
                id="master-task-list-toggle"
                type="button"
                class="report-card-toggle"
                :aria-expanded="taskListOpen"
                @click="toggleTaskList">
                <span class="report-card-heading">
                    <span class="report-card-title">Master List of Tasks</span>
                    <span class="report-card-desc">
                        Download every to-do the system would show for one employer and plan year
                        (Action Center next step, Planning Guide, Calendar, Ongoing Requirements, and Gap action items),
                        plus that employer’s activity log and the agency’s Content Library / Transform latest activity.
                    </span>
                </span>
                <i class="bi text-primary" :class="taskListOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </button>
            <div v-show="taskListOpen" class="report-card-body">
                <form @submit.prevent="downloadTaskList">
                    <div class="form-group">
                        <label class="fw-bold text-gray-700" for="task-agency">Agency</label>
                        <BcsDropdown
                            v-model="selectedAgencyId"
                            id="task-agency"
                            wide
                            :dropdown-options="agencyOptions"
                            :loading="agenciesLoading"
                            :disabled="!agenciesLoading && agencyOptions.length === 0"
                            :placeholder="agenciesLoading ? 'Loading agencies...' : 'Select an agency'"
                            @change="onAgencyChange">
                        </BcsDropdown>
                    </div>
                    <div class="form-group">
                        <label class="fw-bold text-gray-700" for="task-employer">Employer</label>
                        <BcsDropdown
                            v-model="selectedEmployerId"
                            id="task-employer"
                            wide
                            :dropdown-options="employerOptions"
                            :loading="employersLoading"
                            :disabled="!employersLoading && employerOptions.length === 0"
                            :placeholder="employersLoading ? 'Loading employers...' : 'Select an employer'"
                            @change="onEmployerChange">
                        </BcsDropdown>
                    </div>
                    <div class="form-group">
                        <label class="fw-bold text-gray-700" for="task-plan-year">Plan Year</label>
                        <BcsDropdown
                            v-model="selectedPlanYearId"
                            id="task-plan-year"
                            wide
                            :dropdown-options="planYearOptions"
                            :disabled="planYearOptions.length === 0"
                            placeholder="Select a plan year">
                        </BcsDropdown>
                    </div>
                    <button
                        id="download-master-task-list"
                        type="submit"
                        :class="{ 'disabled': !canDownloadTaskList }"
                        :disabled="!canDownloadTaskList"
                        class="btn btn-primary btn-action-profile d-flex justify-content-center align-items-center">
                        <span v-if="taskListLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        {{ taskListLoading ? 'Generating...' : 'Download Excel' }}
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>


<style scoped lang="scss">
:deep {
    .dropdown {
        button {
            border: 1px solid var(--bs-gray-300) !important;
        }

        button,
        ul {
            width: 100% !important;
        }
    }
}

.reports-page {
    max-width: 860px;
    margin: 0 auto;
    padding: 20px 0 40px;
}

.report-card {
    margin-bottom: 16px;
    border: 1px solid var(--bs-gray-300);
    border-radius: 8px;
}

.report-card:has(.dropdown-menu.show) {
    z-index: 2;
}

.report-card-toggle {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    padding: 16px 20px;
    background: white;
    border: 0;
    border-radius: 8px;
    text-align: left;
    cursor: pointer;
}

.report-card-toggle[aria-expanded="true"] {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

.report-card-heading {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.report-card-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: var(--bs-gray-800);
}

.report-card-desc {
    color: var(--bs-gray-600);
    font-size: 14px;
    line-height: 1.4;
}

.report-card-toggle .bi {
    flex-shrink: 0;
    font-size: 1.1rem;
    margin-top: 4px;
}

.report-card-body {
    padding: 0 20px 20px;
    border-top: 1px solid var(--bs-gray-200);
}

form {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin: 16px 0 0;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.no-results {
    padding: 20px;
    background-color: #ffebee;
    border-radius: 4px;
    color: var(--bs-danger);
}

.report {
    margin-top: 20px;
    padding: 20px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.report-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

table {
    border-collapse: collapse;
    margin-top: 15px;

    th,
    td {
        padding: 12px;
        text-align: left;
        border-bottom: 1px solid #ddd;
    }

    th {
        background-color: #ced4da;
        color: white;
    }
}
</style>